<template>
  <div class="client-view">
    <HeaderBar :account="account" />

    <SubHeaderBar
      :areas="areas"
      :selectedArea="selectedArea"
      :config="currentSubHeaderConfig"

      :showAdminSiteFilters="isAdminSiteFilterMode"
      :clientOptions="clientOptions"
      :agencyOptions="agencyOptions"
      :selectedClientId="selectedClientId"
      :selectedAgencyId="selectedAgencyId"

      @client-change="onClientFilterChange"
      @agency-change="onAgencyFilterChange"
      @area-change="onAreaChange"
      @action="handleSubHeaderAction"
      @date-change="handleDateChange"
    />

    <div class="content-body">
      <LeftBar
        class="left-area"
        :menus="menus"
        :activeMenu="currentMenu"
        @select="handleMenu"
      />

      <div class="center-area" v-if="currentMenu === 'main'">
        <SitePlanView
          :imageUrl="currentSiteImage"
          :siteId="selectedArea?.id"
        />
      </div>

      <RightBar
        v-if="currentMenu === 'main'"
        class="right-area"
        :menus="rightMenus"
        :activeMenu="currentMenu"
        :siteImages="currentSiteInfoImages"
        :emergencyNotices="filteredEmergencyNotices"
        @select="handleMenu"
      />

      <div v-if="currentMenu === 'sensor'" class="sensor-layout">
        <div v-if="isSensorListLoading" class="sensor-loading-panel">
          <div class="loading-spinner"></div>
          <div class="loading-title">현장에 배치된 센서 정보를 가져오는 중입니다.</div>
          <div class="loading-desc">잠시만 기다려주세요.</div>
        </div>

        <template v-else>
          <SensorList
            :parts="filteredSensorParts"
            :siteName="selectedArea?.name"
            @toggle-part="handleTogglePart"
            @toggle-sensor="handleToggleSensor"
          />

          <div v-if="isGraphLoading" class="sensor-data-loading-panel">
            <div class="loading-spinner"></div>
            <div class="loading-title">데이터를 불러오는 중입니다.</div>
            <div class="loading-desc">그래프와 테이블을 준비하고 있습니다.</div>
          </div>

          <!-- 기존 단일 센서 시간 그래프 -->
          <ChartArea
            v-else-if="selectedItemType === 'SENSOR' && chartData.length"
            :chartData="chartData"
          />

          <!-- 신규 다단센서 프로파일 그래프 -->
          <MultiSensorProfileArea
            v-else-if="selectedItemType === 'GROUP' && multiSensorProfileData.length"
            :profileData="multiSensorProfileData"
            :sensorGroupMeta="selectedSensorGroupMeta"
            :siteName="selectedArea?.name"
            :selectedName="selectedSensors[0]?.sensorName"
          />

          <div v-else class="sensor-empty-panel">
            선택된 센서가 없습니다.
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<script>
import HeaderBar from '@/components/common/HeaderBar.vue'
import SubHeaderBar from '@/components/client-ui/layout/SubHeaderBar.vue'
import LeftBar from '@/components/common/LeftBar.vue'
import SitePlanView from '@/components/client-ui/plan/SitePlanView.vue'
import RightBar from '@/components/client-ui/layout/RightBar.vue'
import SensorList from '@/components/client-ui/sensor/SensorList.vue'
import ChartArea from '@/components/client-ui/chart/ChartArea.vue'
import MultiSensorProfileArea from '@/components/client-ui/chart/MultiSensorProfileArea.vue'

import api from '@/api/axios'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'ClientView',

  components: {
    HeaderBar,
    SubHeaderBar,
    LeftBar,
    SitePlanView,
    RightBar,
    SensorList,
    ChartArea,
    MultiSensorProfileArea
  },

  data() {
    return {
      dateRange: {
        from: null,
        to: null
      },

      currentMenu: 'main',
      selectedArea: null,

      areas: [],

      clientOptions: [],
      agencyOptions: [],
      selectedClientId: null,
      selectedAgencyId: null,

      subHeaderConfigMap: {
        main: {
          type: 'main'
        },

        sensor: {
          type: 'sensor',
          showDateRange: true,
          actions: ['기간변경', 'CSV 출력', '보고서 출력']
        }
      },

      sensorParts: [],
      sensorDataMap: {},

      /*
       * 신규: 다단센서 프로파일 데이터
       *
       * 백엔드 응답:
       * /app/sensorMeasurement/profile/group
       *
       * result.profileList 를 이 배열에 넣는다.
       */
      multiSensorProfileData: [],

      /*
       * 신규: 다단센서 그룹 메타 정보
       *
       * 백엔드 응답 result 전체를 보관한다.
       * - chartMin
       * - chartMax
       * - thresholdLines
       * - profileAxis
       * - sensorGroupName
       */
      selectedSensorGroupMeta: null,

      siteDetailMap: {},
      emergencyNotices: [],

      isSensorListLoading: false,
      isGraphLoading: false,
      sensorFetchToken: 0,

      isFetchingSensorList: false,
      isFetchingMultiSensorProfile: false,
    }
  },

  computed: {
    authStore() {
      return useAuthStore()
    },

    account() {
      return {
        id: this.authStore.user?.username || '',
        name: this.selectedArea?.name || this.authStore.user?.roleName || '고객 시스템',
        mode: 'client'
      }
    },

    menus() {
      const baseMenus = [
        {
          key: 'main',
          label: '메인 화면',
          icon: 'fa-solid fa-house'
        },
        {
          key: 'sensor',
          label: '센서 데이터 조회',
          icon: 'fa-solid fa-chart-line'
        }
      ]

      if (this.authStore.isAdmin) {
        baseMenus.push({
          key: 'goAdmin',
          label: '관리자 화면',
          icon: 'fa-solid fa-user-shield'
        })
      }

      return baseMenus
    },

    rightMenus() {
      return this.menus.filter(menu => ['main', 'sensor'].includes(menu.key))
    },

    currentSubHeaderConfig() {
      return this.subHeaderConfigMap[this.currentMenu] || this.subHeaderConfigMap.main
    },

    currentSiteDetail() {
      if (!this.selectedArea?.id) return null
      return this.siteDetailMap[this.selectedArea.id] || null
    },

    currentSiteImage() {
      const drawingAttachFileNo = this.currentSiteDetail?.drawingAttachFileNo
      if (!drawingAttachFileNo) return ''
      return `/app/file/image/${drawingAttachFileNo}`
    },

    currentSiteInfoImages() {
      return this.currentSiteDetail?.siteMainImageList || []
    },

    filteredEmergencyNotices() {
      if (!this.selectedArea?.id) return []

      return (this.emergencyNotices || []).filter(notice => {
        const siteIds = Array.isArray(notice?.siteIds) ? notice.siteIds : []
        return siteIds.some(siteId => Number(siteId) === Number(this.selectedArea.id))
      })
    },

    filteredSensorParts() {
      return this.sensorParts || []
    },

    hasSelectedSensor() {
      return this.filteredSensorParts.some(group =>
        (group.subGroups || []).some(subGroup =>
          (subGroup.sensors || []).some(sensor => sensor.checked)
        )
      )
    },

    selectedSensors() {
      return this.filteredSensorParts
        .flatMap(group => group.subGroups || [])
        .flatMap(subGroup => subGroup.sensors || [])
        .filter(sensor => sensor.checked)
    },

    selectedItemType() {
      const selected = this.selectedSensors[0]
      return selected?.itemType || null
    },

    allSensorItems() {
      return this.filteredSensorParts
        .flatMap(group => group.subGroups || [])
        .flatMap(subGroup => subGroup.sensors || [])
    },

    chartData() {
      const selected = this.selectedSensors[0]
      if (!selected) return []

      if (selected.itemType === 'GROUP') {
        return []
      }

      return this.buildConnectedSensorChartData(selected)
    },

    isAdminSiteFilterMode() {
      const roleCode =
        this.authStore.user?.roleCode ||
        this.authStore.user?.userRoleCode ||
        this.authStore.user?.roleName ||
        ''

      return (
        this.authStore.isAdmin ||
        roleCode === 'SUPER_ADMIN' ||
        roleCode === 'ADMIN' ||
        roleCode === '최고관리자' ||
        roleCode === '관리자'
      )
    },
  },

  async mounted() {
    const now = new Date()
    const oneWeeksAgo = new Date(now)
    oneWeeksAgo.setDate(oneWeeksAgo.getDate() - 7)

    this.dateRange.from = oneWeeksAgo
    this.dateRange.to = now

    if (this.isAdminSiteFilterMode) {
      await this.fetchAdminFilterOptions()
    }

    await this.fetchSiteList()
    await this.fetchEmergencyNoticeList()

    if (this.areas.length > 0) {
      const routeSiteId = Number(this.$route.query.id)
      const matchedArea = this.areas.find(area => area.id === routeSiteId)

      this.selectedArea = matchedArea || this.areas[0]

      if (this.selectedArea?.id) {
        await this.fetchSiteDetail(this.selectedArea.id)
        await this.fetchSensorList()
      }
    }
  },

  methods: {
    handleMenu(menuKey) {
      if (menuKey === 'goAdmin') {
        this.$router.push('/admin')
        return
      }

      this.currentMenu = menuKey
    },

    async onAreaChange(area) {
      const selectedArea =
        area && typeof area === 'object'
          ? area
          : this.areas.find(item => String(item.id) === String(area)) || null

      this.selectedArea = selectedArea

      // 현장 변경 시 이전 현장의 센서/그래프가 보이지 않도록 즉시 초기화
      this.sensorParts = []
      this.sensorDataMap = {}
      this.multiSensorProfileData = []
      this.selectedSensorGroupMeta = null
      this.isGraphLoading = false

      if (this.selectedArea?.id) {
        await this.fetchSiteDetail(this.selectedArea.id)
      }

      await this.fetchSensorList()
    },

    async handleDateChange(dateRange) {
      this.dateRange = dateRange
    },

    async handleSubHeaderAction(action) {
      if (action === '기간변경' || action === '확인') {
        await this.fetchSensorList()

        const selected = this.selectedSensors[0]
        if (selected?.itemType === 'GROUP') {
          await this.fetchMultiSensorProfile(selected.sensorGroupId)
        }

        return
      }

      if (action === 'CSV 출력') {
        await this.downloadCsv()
        return
      }

      if (action === '보고서 출력') {
        this.printReport()
      }
    },

    handleTogglePart(groupId) {
      this.sensorParts = this.sensorParts.map(group => {
        if (group.groupId !== groupId) return group

        return {
          ...group,
          expanded: !group.expanded
        }
      })
    },

    async handleToggleSensor(payload) {
      if (!payload || this.isSensorListLoading) return

      this.isGraphLoading = true

      const selectedItemKey = payload.itemKey

      const currentSelected = this.selectedSensors[0]
      const isSameItem =
        currentSelected && currentSelected.itemKey === selectedItemKey

      this.sensorParts = this.sensorParts.map(group => ({
        ...group,
        subGroups: (group.subGroups || []).map(subGroup => ({
          ...subGroup,
          sensors: (subGroup.sensors || []).map(sensor => ({
            ...sensor,
            checked: isSameItem ? false : sensor.itemKey === selectedItemKey
          }))
        }))
      }))

      if (isSameItem) {
        this.multiSensorProfileData = []
        this.selectedSensorGroupMeta = null
        this.isGraphLoading = false
        return
      }

      try {
        if (payload.itemType === 'GROUP') {
          await this.fetchMultiSensorProfile(payload.sensorGroupId)
        } else {
          this.multiSensorProfileData = []
          this.selectedSensorGroupMeta = null
        }

        await this.$nextTick()

        await new Promise(resolve => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve)
          })
        })
      } finally {
        this.isGraphLoading = false
      }
    },

    resolveSensorKind(chart) {
      return String(chart?.displayType || '').trim().toUpperCase()
    },

    normalizeChartDisplayMeta(chart) {
      const kind = this.resolveSensorKind(chart)

      if (kind === 'LC') {
        return {
          ...chart,
          showAmount: true,
          showDeg: false,
          showMm: false,
          showAngular: false,
          showDiff: true,

          amountLabel: '하중(Ton)',
          previousDiffLabel: '전회대비(Ton)',
          initialDiffLabel: '초기대비(Ton)',

          chartValueKey: 'amount'
        }
      }

      if (kind === 'V') {
        return {
          ...chart,

          showAmount: true,
          showDeg: false,
          showMm: false,
          showAngular: false,
          showDiff: false,

          amountLabel: 'PVS(kine)',

          // 진동계는 amount 그대로
          chartValueKey: 'amount'
        }
      }

      if (kind === 'FM') {
        return {
          ...chart,
          showAmount: true,
          showDeg: false,
          showMm: false,
          showAngular: false,
          showDiff: true,

          amountLabel: '유량(m³)',
          previousDiffLabel: '전회대비(m³)',
          initialDiffLabel: '초기대비(m³)',

          // 유량계 그래프는 유량값 그대로
          chartValueKey: 'amount'
        }
      }

      if (kind === 'WM') {
        return {
          ...chart,

          showAmount: true,
          showDeg: false,
          showMm: false,
          showAngular: false,

          // 전회대비 / 초기대비 표시
          showDiff: true,

          amountLabel: '지하수위(GL.m)',
          previousDiffLabel: '전회대비(GL.m)',
          initialDiffLabel: '초기대비(GL.m)',

          // 지하수위계 그래프는 측정치가 아니라 초기대비를 그린다.
          chartValueKey: 'initialDiff'
        }
      }

      if (kind === 'CM') {
        return {
          ...chart,
          showAmount: false,
          showDeg: false,
          showMm: true,
          showAngular: false,
          showDiff: true,
          mmLabel: '측정치(mm)',

          // 균열측정계 그래프는 측정치가 아니라 초기대비(mm)를 그린다.
          chartValueKey: 'initialDiff'
        }
      }

      if (kind === 'GA') {
        return {
          ...chart,
          showAmount: true,
          showDeg: false,
          showMm: true,
          showAngular: false,
          showDiff: true,
          amountLabel: '측정치(V)',
          mmLabel: '측정치(mm)',
          chartValueKey: 'mm'
        }
      }

      if (kind === 'R') {
        return {
          ...chart,
          showAmount: false,
          showDeg: true,
          showMm: true,
          showAngular: false,
          showDiff: false,
          degLabel: '각도값(deg)',
          mmLabel: '누적 처짐량(mm)',
          chartValueKey: 'mm'
        }
      }

      return {
        ...chart,
        showAmount: true,
        showDeg: true,
        showMm: true,
        showAngular: true,
        showDiff: false,
        amountLabel: '측정치',
        degLabel: '변화량(deg)',
        mmLabel: '변위량(mm)',
        angularLabel: '각변위',
        chartValueKey: 'mm'
      }
    },

    async fetchSiteList() {
      try {
        let res

        if (this.isAdminSiteFilterMode) {
          const params = {
            deleted: false,
            orderBy: 'siteNameAsc',
          }

          if (this.selectedClientId) {
            params.clientId = this.selectedClientId
          }

          if (this.selectedAgencyId) {
            params.agencyId = this.selectedAgencyId
          }

          res = await api.get('/app/site/list', {
            params,
          })
        } else {
          res = await api.get('/app/site/myList')
        }

        const list = Array.isArray(res?.data?.result)
          ? res.data.result
          : []

        this.areas = list.map(item => ({
          id: item.id,
          name: item.name || item.siteName,
        }))
      } catch (e) {
        console.error('fetchSiteList error', e)
        this.areas = []
      }
    },

    async fetchEmergencyNoticeList() {
      try {
        const res = await api.get('/app/emergencyNotice/clientList')

        this.emergencyNotices = Array.isArray(res?.data?.result)
          ? res.data.result
          : []
      } catch (e) {
        console.error('fetchEmergencyNoticeList error', e)
        this.emergencyNotices = []
      }
    },

    async fetchSiteDetail(siteId) {
      if (!siteId) return

      try {
        const res = await api.get('/app/site/detail', {
          params: {
            id: siteId
          }
        })

        const detail = res?.data?.result || null

        this.siteDetailMap = {
          ...this.siteDetailMap,
          [siteId]: detail
        }
      } catch (e) {
        console.error('fetchSiteDetail error', e)
        this.siteDetailMap = {
          ...this.siteDetailMap,
          [siteId]: null
        }
      }
    },

    normalizeSensorTreeItem(sensor, group, subGroup, prevSelectedItemKey) {
      const rawType =
        sensor.itemType ||
        sensor.assignmentType ||
        sensor.type ||
        sensor.sensorType ||
        'SENSOR'

      const itemType = String(rawType).toUpperCase() === 'GROUP'
        ? 'GROUP'
        : 'SENSOR'

      const sensorId =
        sensor.sensorId ??
        sensor.id ??
        null

      const sensorGroupId =
        sensor.sensorGroupId ??
        sensor.groupId ??
        sensor.id ??
        null

      const itemId = itemType === 'GROUP' ? sensorGroupId : sensorId
      const itemKey = `${itemType}_${itemId}`

      return {
        ...sensor,

        itemType,
        itemKey,

        sensorId: itemType === 'SENSOR' ? itemId : null,
        sensorGroupId: itemType === 'GROUP' ? itemId : null,

        sensorName:
          sensor.sensorName ||
          sensor.sensorGroupName ||
          sensor.groupName ||
          sensor.displayName ||
          sensor.name ||
          (itemType === 'GROUP' ? `다단센서-${itemId}` : `센서-${itemId}`),

        groupName: group.groupName,
        subGroupName: subGroup.subGroupName,

        sortOrder: sensor.sortOrder ?? 0,
        checked: prevSelectedItemKey !== null && prevSelectedItemKey === itemKey
      }
    },

    async fetchSensorList() {
      if (!this.selectedArea?.id) {
        this.sensorParts = []
        this.sensorDataMap = {}
        return
      }

      const fetchToken = ++this.sensorFetchToken

      this.isFetchingSensorList = true
      this.isSensorListLoading = true
      this.isGraphLoading = false

      // 이전 현장의 센서 목록이 계속 보이지 않도록 즉시 비운다.
      this.sensorParts = []
      this.sensorDataMap = {}
      this.multiSensorProfileData = []
      this.selectedSensorGroupMeta = null

      try {
        const res = await api.get('/app/sensor/graphDataList', {
          params: {
            siteId: this.selectedArea.id,
            startAt: this.formatApiDate(this.dateRange.from),
            endAt: this.formatApiDate(this.dateRange.to)
          }
        })

        if (fetchToken !== this.sensorFetchToken) return

        const result = res?.data?.result ?? {}
        const sensorTree = result.sensorTree ?? []

        this.sensorParts = sensorTree.map(group => ({
          groupId: group.groupId,
          groupName: group.groupName,
          sortOrder: group.sortOrder ?? 0,
          expanded: true,
          subGroups: (group.subGroups ?? []).map(subGroup => ({
            subGroupId: subGroup.subGroupId,
            subGroupName: subGroup.subGroupName,
            sortOrder: subGroup.sortOrder ?? 0,
            sensors: (subGroup.sensors ?? []).map(sensor =>
              this.normalizeSensorTreeItem(sensor, group, subGroup, null)
            )
          }))
        }))

        this.sensorDataMap = result.sensorDataMap ?? {}
      } catch (e) {
        console.error('fetchSensorList error', e)
        this.sensorParts = []
        this.sensorDataMap = {}
      } finally {
        if (fetchToken === this.sensorFetchToken) {
          this.isFetchingSensorList = false
          this.isSensorListLoading = false
        }
      }
    },

    async fetchMultiSensorProfile(sensorGroupId) {
      if (!sensorGroupId || !this.selectedArea?.id) {
        this.multiSensorProfileData = []
        this.selectedSensorGroupMeta = null
        return
      }

      if (this.isFetchingMultiSensorProfile) {
        return
      }

      this.isFetchingMultiSensorProfile = true

      try {
        /*
         * 다단센서 그룹 기준 프로파일 조회
         *
         * 기존 임시 구조:
         * - /app/sensorGroup/detail
         * - /app/sensorMeasurement/profile?sensorIds=...
         *
         * 변경 구조:
         * - /app/sensorMeasurement/profile/group
         * - 백엔드에서 sensor_group_item / sensor_group_config / sensor_measurement를 모두 조합
         */
        const profileRes = await api.get('/app/sensorMeasurement/profile/group', {
          params: {
            siteId: this.selectedArea.id,
            sensorGroupId,
            startAt: this.formatApiDateForProfile(this.dateRange.from),
            endAt: this.formatApiDateForProfile(this.dateRange.to)
          }
        })

        const result = profileRes?.data?.result || null

        this.selectedSensorGroupMeta = result
        this.multiSensorProfileData = Array.isArray(result?.profileList)
          ? result.profileList
          : []
      } catch (e) {
        console.error('fetchMultiSensorProfile error', e)
        this.multiSensorProfileData = []
        this.selectedSensorGroupMeta = null
      } finally {
        this.isFetchingMultiSensorProfile = false
      }
    },

    async downloadCsv() {
      const selected = this.selectedSensors[0]
      if (!selected) {
        alert('출력할 센서를 선택하세요.')
        return
      }

      const siteId = this.selectedArea?.id
      const startAt = this.formatApiDate(this.dateRange.from)
      const endAt = this.formatApiDate(this.dateRange.to)

      try {
        let url = ''
        let params = {}

        // 🔥 다단센서
        if (selected.itemType === 'GROUP') {
          url = '/app/sensorMeasurement/profile/group/excel'
          params = {
            siteId,
            sensorGroupId: selected.sensorGroupId,
            startAt,
            endAt
          }
        }
        // 🔹 단일센서
        else {
          url = '/app/sensor/createExcel'
          params = {
            siteId,
            sensorId: selected.sensorId,
            startAt,
            endAt
          }
        }

        const res = await api.get(url, {
          params,
          responseType: 'blob'
        })

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        const fileName =
          selected.itemType === 'GROUP'
            ? `${selected.sensorName || '다단센서'}_측정데이터.xlsx`
            : `${selected.sensorName || 'sensor'}_측정데이터.xlsx`

        const urlObject = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = urlObject
        link.download = fileName.replace(/[\\/:*?"<>|]/g, '_')

        document.body.appendChild(link)
        link.click()
        link.remove()

        window.URL.revokeObjectURL(urlObject)
      } catch (e) {
        console.error('downloadCsv error', e)
        alert('엑셀 출력 중 오류 발생')
      }
    },

    printReport() {
      const selected = this.selectedSensors[0]
      if (!selected) {
        alert('출력할 센서를 선택하세요.')
        return
      }

      // 다단센서는 MultiSensorProfileArea.vue의 canvas + table을 기준으로 별도 보고서를 출력한다.
      if (selected.itemType === 'GROUP') {
        this.printMultiSensorReport(selected)
        return
      }

      const charts = this.chartData || []
      if (!charts.length) {
        alert('출력할 차트 데이터가 없습니다.')
        return
      }

      const mainChart = charts[0]
      const chartImage = this.getChartImage()

      // 핵심: 화면에 이미 정상 출력된 테이블을 그대로 사용
      const reportRoot = document.getElementById('report-capture')
      const tableHtml = reportRoot?.querySelector('.data-table')?.outerHTML || ''

      const siteName = this.selectedArea?.name ?? ''
      const sensorName = selected.sensorName ?? ''
      const groupName = mainChart.groupName ?? ''
      const subGroupName = mainChart.subGroupName ?? ''
      const from = this.formatPrintDate(this.dateRange.from)
      const to = this.formatPrintDate(this.dateRange.to)

      if (!chartImage && !tableHtml) {
        alert('출력할 그래프/테이블 데이터를 찾을 수 없습니다.')
        return
      }

      const printWindow = window.open('', '_blank', 'width=1400,height=1000')

      if (!printWindow) {
        alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도하세요.')
        return
      }

      printWindow.document.open()
      printWindow.document.write(`
        <html>
          <head>
            <title>${this.escapeHtml(sensorName)} 보고서</title>
            <style>
              * { box-sizing: border-box; }

              body {
                margin: 0;
                padding: 24px;
                font-family: Arial, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif;
                color: #222;
                background: #fff;
              }

              .report-wrap { width: 100%; }

              .report-header { margin-bottom: 18px; }

              .report-title {
                font-size: 28px;
                font-weight: 700;
                text-align: center;
                margin-bottom: 18px;
              }

              .meta-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 13px;
              }

              .meta-table td {
                border: 1px solid #cfcfcf;
                padding: 8px 10px;
              }

              .meta-label {
                width: 120px;
                font-weight: 700;
                background: #f5f5f5;
              }

              .section-title {
                font-size: 15px;
                font-weight: 700;
                margin: 18px 0 10px;
              }

              .chart-box {
                width: 100%;
                border: 1px solid #d9dee5;
                padding: 12px;
                border-radius: 8px;
                margin-bottom: 20px;
              }

              .chart-image {
                width: 100%;
                max-width: 100%;
                display: block;
              }

              .data-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 11px;
              }

              .data-table th,
              .data-table td {
                border: 1px solid #cfcfcf;
                padding: 5px 6px;
                text-align: center;
                white-space: nowrap;
              }

              .data-table thead th {
                background: #f5f5f5;
                font-weight: 700;
              }

              .initial-row td {
                background: #f9fbff;
                font-weight: 600;
              }

              .sticky-col,
              .sticky-head {
                position: static !important;
              }

              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="report-wrap">
              <div class="report-header">
                <div class="report-title">${this.escapeHtml(sensorName)} 보고서</div>

                <table class="meta-table">
                  <tr>
                    <td class="meta-label">현장명</td>
                    <td>${this.escapeHtml(siteName)}</td>
                    <td class="meta-label">대분류</td>
                    <td>${this.escapeHtml(groupName)}</td>
                  </tr>
                  <tr>
                    <td class="meta-label">소분류</td>
                    <td>${this.escapeHtml(subGroupName)}</td>
                    <td class="meta-label">조회 기간</td>
                    <td>${this.escapeHtml(from)} ~ ${this.escapeHtml(to)}</td>
                  </tr>
                </table>
              </div>

              <div class="section-title">그래프</div>
              <div class="chart-box">
                ${chartImage ? `<img class="chart-image" src="${chartImage}" alt="chart" />` : '<div>차트 이미지가 없습니다.</div>'}
              </div>

              <div class="section-title">테이블</div>
              ${tableHtml || '<div>테이블 데이터가 없습니다.</div>'}
            </div>
          </body>
        </html>
      `)

      printWindow.document.close()
      printWindow.focus()

      setTimeout(() => {
        printWindow.print()
      }, 500)
    },

    printMultiSensorReport(selected) {
      const root = document.getElementById('multi-profile-report-capture')
      if (!root) {
        alert('출력할 다단센서 프로파일 영역을 찾을 수 없습니다.')
        return
      }

      const canvas = root.querySelector('canvas')
      let chartImage = ''

      if (canvas && typeof canvas.toDataURL === 'function') {
        try {
          chartImage = canvas.toDataURL('image/png')
        } catch (e) {
          console.error('printMultiSensorReport canvas error', e)
          chartImage = ''
        }
      }

      const tableHtml = root.querySelector('.profile-table')?.outerHTML || ''
      if (!chartImage && !tableHtml) {
        alert('출력할 다단센서 데이터가 없습니다.')
        return
      }

      const siteName = this.selectedArea?.name ?? ''
      const sensorName =
        selected?.sensorName ||
        this.selectedSensorGroupMeta?.sensorGroupName ||
        '다단센서'

      const profileAxis = this.selectedSensorGroupMeta?.profileAxis || ''
      const groupName = this.selectedSensorGroupMeta?.sensorGroupName || sensorName
      const from = this.formatPrintDate(this.dateRange.from)
      const to = this.formatPrintDate(this.dateRange.to)

      const printWindow = window.open('', '_blank', 'width=1400,height=1000')

      if (!printWindow) {
        alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도하세요.')
        return
      }

      printWindow.document.open()
      printWindow.document.write(`
        <html>
          <head>
            <title>${this.escapeHtml(sensorName)} 보고서</title>
            <style>
              * { box-sizing: border-box; }
              body {
                margin: 0;
                padding: 24px;
                font-family: Arial, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif;
                color: #222;
                background: #fff;
              }
              .report-wrap { width: 100%; }
              .report-header { margin-bottom: 18px; }
              .report-title {
                font-size: 28px;
                font-weight: 700;
                text-align: center;
                margin-bottom: 18px;
              }
              .meta-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 13px;
              }
              .meta-table td {
                border: 1px solid #cfcfcf;
                padding: 8px 10px;
              }
              .meta-label {
                width: 120px;
                font-weight: 700;
                background: #f5f5f5;
              }
              .section-title {
                font-size: 15px;
                font-weight: 700;
                margin: 18px 0 10px;
              }
              .chart-box {
                width: 100%;
                border: 1px solid #d9dee5;
                padding: 12px;
                border-radius: 8px;
                margin-bottom: 20px;
              }
              .chart-image {
                width: 100%;
                max-width: 100%;
              }
              .profile-table,
              .data-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 12px;
              }
              .profile-table th,
              .profile-table td,
              .data-table th,
              .data-table td {
                border: 1px solid #cfcfcf;
                padding: 6px 8px;
                text-align: center;
              }
              .profile-table thead th,
              .data-table thead th {
                background: #f5f5f5;
                font-weight: 700;
              }
            </style>
          </head>
          <body>
            <div class="report-wrap">
              <div class="report-header">
                <div class="report-title">${this.escapeHtml(sensorName)} 보고서</div>

                <table class="meta-table">
                  <tr>
                    <td class="meta-label">현장명</td>
                    <td>${this.escapeHtml(siteName)}</td>
                    <td class="meta-label">센서그룹</td>
                    <td>${this.escapeHtml(groupName)}</td>
                  </tr>
                  <tr>
                    <td class="meta-label">프로파일 축</td>
                    <td>${this.escapeHtml(profileAxis)}</td>
                    <td class="meta-label">조회 기간</td>
                    <td>${this.escapeHtml(from)} ~ ${this.escapeHtml(to)}</td>
                  </tr>
                </table>
              </div>

              <div class="section-title">그래프</div>
              <div class="chart-box">
                ${chartImage ? `<img class="chart-image" src="${chartImage}" alt="multi-sensor-chart" />` : '<div>차트 이미지가 없습니다.</div>'}
              </div>

              <div class="section-title">테이블</div>
              ${tableHtml || '<div>테이블 데이터가 없습니다.</div>'}
            </div>
          </body>
        </html>
      `)

      printWindow.document.close()
      printWindow.focus()

      setTimeout(() => {
        printWindow.print()
      }, 300)
    },

    getChartImage() {
      const root = document.getElementById('report-capture')
      if (!root) return ''

      const canvas = root.querySelector('canvas')
      if (!canvas || typeof canvas.toDataURL !== 'function') return ''

      try {
        return canvas.toDataURL('image/png')
      } catch (e) {
        console.error('getChartImage error', e)
        return ''
      }
    },

    buildPrintTableRows(charts) {
      const chartList = Array.isArray(charts) ? charts : [charts].filter(Boolean)
      if (!chartList.length) return []

      const mainChart = chartList[0]
      const labels = Array.isArray(mainChart?.labels) ? mainChart.labels : []

      const rowsAsc = labels.map((time, idx) => {
        const row = {
          no: idx + 1,
          time,
          remark: ''
        }

        chartList.forEach(chart => {
          const val = chart.values?.[idx] ?? {}

          row[`${chart.sensorId}_amount`] = chart.showAmount
            ? this.formatNumberForPrint(val.amount)
            : null

          row[`${chart.sensorId}_deg`] = chart.showDeg
            ? this.formatNumberForPrint(val.deg)
            : null

          row[`${chart.sensorId}_mm`] = chart.showMm
            ? this.formatNumberForPrint(val.mm)
            : null

          row[`${chart.sensorId}_angular`] = chart.showAngular
            ? this.formatAngularForPrint(val)
            : null

          row[`${chart.sensorId}_previousDiff`] = chart.showDiff
            ? this.formatNumberForPrint(val.previousDiff)
            : null

          row[`${chart.sensorId}_initialDiff`] = chart.showDiff
            ? this.formatNumberForPrint(val.initialDiff)
            : null
        })

        return row
      })

      const rowsDesc = [...rowsAsc].reverse().map((row, index) => ({
        ...row,
        no: index + 1
      }))

      const hasBasePoint = chartList.some(chart => chart?.basePoint)

      if (hasBasePoint) {
        const initialRow = {
          no: '',
          time: mainChart.baseMeasuredAt || '',
          remark: '초기치'
        }

        chartList.forEach(chart => {
          const basePoint = chart.basePoint ?? {}

          initialRow[`${chart.sensorId}_amount`] = chart.showAmount
            ? this.formatNumberForPrint(basePoint.amount)
            : null

          initialRow[`${chart.sensorId}_deg`] = chart.showDeg
            ? this.formatNumberForPrint(0)
            : null

          initialRow[`${chart.sensorId}_mm`] = chart.showMm
            ? this.formatNumberForPrint(0)
            : null

          initialRow[`${chart.sensorId}_angular`] = chart.showAngular
            ? '1/0'
            : null

          initialRow[`${chart.sensorId}_previousDiff`] = chart.showDiff
            ? this.formatNumberForPrint(0)
            : null

          initialRow[`${chart.sensorId}_initialDiff`] = chart.showDiff
            ? this.formatNumberForPrint(0)
            : null
        })

        return [initialRow, ...rowsDesc]
      }

      return rowsDesc
    },

    buildPrintTableHtml(charts, rows) {
      const chartList = Array.isArray(charts) ? charts : [charts].filter(Boolean)

      const sensorHeadHtml = chartList.map(chart => {
        const colspan = this.getPrintVisibleColumnCount(chart)
        return `<th colspan="${colspan}">${this.escapeHtml(chart.sensorName || '')}</th>`
      }).join('')

      const subHeadHtml = chartList.map(chart => {
        const columns = this.getPrintColumns(chart)

        return columns
          .map(col => `<th>${this.escapeHtml(col.label)}</th>`)
          .join('')
      }).join('')

      const body = rows.map(row => `
        <tr${row.remark === '초기치' ? ' style="background:#f9fbff;font-weight:600;"' : ''}>
          <td>${row.no || ''}</td>
          <td>${this.escapeHtml(row.time)}</td>
          ${chartList.map(chart => {
            const columns = this.getPrintColumns(chart)

            return columns
              .map(col => `<td>${row[`${chart.sensorId}_${col.key}`] ?? '-'}</td>`)
              .join('')
          }).join('')}
          <td>${this.escapeHtml(row.remark)}</td>
        </tr>
      `).join('')

      return `
        <table class="data-table">
          <thead>
            <tr>
              <th rowspan="2">No.</th>
              <th rowspan="2">측정일시</th>
              ${sensorHeadHtml}
              <th rowspan="2">비고</th>
            </tr>
            <tr>
              ${subHeadHtml}
            </tr>
          </thead>
          <tbody>
            ${body}
          </tbody>
        </table>
      `
    },

    getPrintColumns(chart) {
      const columns = []

      if (chart.showAmount) {
        columns.push({
          key: 'amount',
          label: chart.amountLabel || '측정치'
        })
      }

      if (chart.showDeg) {
        columns.push({
          key: 'deg',
          label: chart.degLabel || '변화량(deg)'
        })
      }

      if (chart.showMm) {
        columns.push({
          key: 'mm',
          label: chart.mmLabel || '변위량(mm)'
        })
      }

      // 균열측정계(CM) 전용
      if (chart.showDiff) {
        columns.push({
          key: 'previousDiff',
          label: '전회대비(mm)'
        })

        columns.push({
          key: 'initialDiff',
          label: '초기대비(mm)'
        })
      }

      if (chart.showAngular) {
        columns.push({
          key: 'angular',
          label: chart.angularLabel || '각변위'
        })
      }

      return columns
    },

    getPrintVisibleColumnCount(chart) {
      return this.getPrintColumns(chart).length
    },

    formatAngularForPrint(value) {
      if (value?.angular !== undefined && value?.angular !== null) {
        return this.formatAngular(value.angular)
      }

      const deg = Number(Number(value?.deg).toFixed(4))
      if (!Number.isFinite(deg) || deg === 0) return '1/0'

      const rad = Math.abs(deg * 0.0175)
      if (!Number.isFinite(rad) || rad === 0) return '1/0'

      return `1/${Math.round(1 / rad)}`
    },

    formatNumberForPrint(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return num.toFixed(4)
    },

    formatPrintDate(input) {
      if (!input) return ''
      const d = input instanceof Date ? input : new Date(input)
      if (Number.isNaN(d.getTime())) return ''

      const yyyy = d.getFullYear()
      const MM = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')

      return `${yyyy}-${MM}-${dd} ${hh}:${mm}`
    },

    formatApiDate(input) {
      if (!input) return ''

      const d = input instanceof Date ? input : new Date(input)
      if (Number.isNaN(d.getTime())) return ''

      const yyyy = d.getFullYear()
      const MM = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      const ss = String(d.getSeconds()).padStart(2, '0')

      return `${yyyy}.${MM}.${dd} ${hh}:${mm}:${ss}`
    },

    /*
    * profile API도 기존 조회 API와 동일하게 yyyy.MM.dd HH:mm:ss 형식으로 전달한다.
    */
    formatApiDateForProfile(input) {
      return this.formatApiDate(input)
    },

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },

    findSensorData(sensor) {
      if (!sensor) return null

      const sensorId =
        sensor.sensorId ??
        sensor.id ??
        null

      if (!sensorId) return null

      return (
        this.sensorDataMap[sensorId] ||
        this.sensorDataMap[String(sensorId)] ||
        null
      )
    },

    getConnectedSensorCode(sensor) {
      const data = this.findSensorData(sensor)

      return (
        sensor?.configValues?.connected_sensor_code ||
        data?.configValues?.connected_sensor_code ||
        sensor?.connectedSensorCode ||
        sensor?.connected_sensor_code ||
        data?.connectedSensorCode ||
        data?.connected_sensor_code ||
        ''
      )
    },

    findSensorByCode(sensorCode) {
      if (!sensorCode) return null

      return this.allSensorItems.find(item => {
        const data = this.findSensorData(item)

        const code =
          item.sensorCode ||
          item.code ||
          data?.sensorCode ||
          data?.code ||
          ''

        return String(code).trim() === String(sensorCode).trim()
      }) || null
    },

    resolveConnectedSensorPair(selected) {
      if (!selected || selected.itemType !== 'SENSOR') {
        return [selected].filter(Boolean)
      }

      const selectedData = this.findSensorData(selected)

      const selectedCode =
        selected.sensorCode ||
        selected.code ||
        selectedData?.sensorCode ||
        selectedData?.code ||
        ''

      /*
      * 1.
      * 정방향 연결
      *
      * IA-1X -> IA-1Y
      */
      const directCode = this.getConnectedSensorCode(selected)
      const directSensor = this.findSensorByCode(directCode)

      if (directSensor) {
        return [selected, directSensor]
      }

      /*
      * 2.
      * 역방향 연결
      *
      * 고객사 방식
      * IA-1Y -> IA-1X
      *
      * 즉,
      * 다른 센서가 현재 센서를 connected_sensor_code 로
      * 가지고 있으면 같이 묶는다.
      */
      const reverseSensor = this.allSensorItems.find(item => {
        if (!item || item.itemType !== 'SENSOR') return false

        // 자기 자신 제외
        if (Number(item.sensorId) === Number(selected.sensorId)) {
          return false
        }

        const connectedCode = this.getConnectedSensorCode(item)

        return (
          selectedCode &&
          String(connectedCode).trim().toUpperCase() ===
          String(selectedCode).trim().toUpperCase()
        )
      })

      if (reverseSensor) {
        return [selected, reverseSensor]
      }

      /*
      * 3.
      * 백엔드 connectedSensorIds fallback
      *
      * 혹시 나중에 DB 방식 바뀌어도 안전하게
      */
      const connectedIds = Array.isArray(selectedData?.connectedSensorIds)
        ? selectedData.connectedSensorIds
        : []

      if (connectedIds.length > 1) {
        const pair = connectedIds
          .map(sensorId =>
            this.allSensorItems.find(item =>
              Number(item.sensorId) === Number(sensorId)
            )
          )
          .filter(Boolean)

        if (pair.length) {
          return pair
        }
      }

      /*
      * 4.
      * 단일 센서 fallback
      */
      return [selected]
    },

    buildNormalizedChartItem(sensor) {
      const data = this.findSensorData(sensor)
      if (!data) return null

      return this.normalizeChartDisplayMeta({
        sensorId: sensor.sensorId,
        sensorName: data.sensorName || sensor.sensorName,
        sensorCode: sensor.sensorCode || data.sensorCode || data.code || '',
        groupName: data.groupName || sensor.groupName || '',
        subGroupName: data.subGroupName || sensor.subGroupName || '',
        labels: data.labels ?? [],
        values: data.values ?? [],
        baseMeasuredAt: data.baseMeasuredAt ?? '',
        basePoint: data.basePoint ?? null,
        displayType: data.displayType,
        showAmount: data.showAmount,
        showDeg: data.showDeg,
        showMm: data.showMm,
        showAngular: data.showAngular,
        amountLabel: data.amountLabel,
        degLabel: data.degLabel,
        mmLabel: data.mmLabel,
        angularLabel: data.angularLabel,
        chartValueKey: data.chartValueKey,
        chartMin: data.chartMin,
        chartMax: data.chartMax,
        thresholdLines: data.thresholdLines ?? [],
        configValues: data.configValues || sensor.configValues || {},
        axisType: data.axisType || data.axis_type || data.configValues?.axis_type || '',
      })
    },

    buildConnectedSensorChartData(selected) {
      const selectedData = this.findSensorData(selected)

      if (this.isV3ChartData(selectedData)) {
        return this.buildV3ChartData(selected)
      }

      const pairSensors = this.resolveConnectedSensorPair(selected)

      return pairSensors
        .map(sensor => this.buildNormalizedChartItem(sensor))
        .filter(Boolean)
    },

    isV3ChartData(data) {
      return String(data?.displayType || '').trim().toUpperCase() === 'V3'
    },

    getAxisTypeFromChart(data) {
      return String(
        data?.axisType ||
        data?.axis_type ||
        data?.configValues?.axis_type ||
        ''
      ).trim().toUpperCase()
    },

    buildV3ChartData(selected) {
      const selectedData = this.findSensorData(selected)

      if (!selectedData) {
        return [this.buildNormalizedChartItem(selected)].filter(Boolean)
      }

      const selectedCode = String(
        selectedData.sensorCode ||
        selectedData.code ||
        selected.sensorCode ||
        selected.code ||
        ''
      ).trim()

      const selectedConnectedCode = String(
        selectedData.connectedSensorCode ||
        selectedData.connected_sensor_code ||
        selectedData.configValues?.connected_sensor_code ||
        selected.connectedSensorCode ||
        selected.connected_sensor_code ||
        selected.configValues?.connected_sensor_code ||
        ''
      ).trim()

      const representativeCode = selectedConnectedCode || selectedCode

      if (!representativeCode) return [this.buildNormalizedChartItem(selected)].filter(Boolean)

      const v3Items = Object.values(this.sensorDataMap || {})
        .filter(data => this.isV3ChartData(data))
        .filter(data => {
          const code = String(data.sensorCode || data.code || '').trim()
          const connected = String(data.connectedSensorCode || data.connected_sensor_code || '').trim()

          return code === representativeCode || connected === representativeCode
        })

      if (!v3Items.length) {
        return [this.buildNormalizedChartItem(selected)].filter(Boolean)
      }

      const axisMap = {}

      v3Items.forEach(data => {
        const axis = this.getAxisTypeFromChart(data)
        if (['X', 'Y', 'Z'].includes(axis)) {
          axisMap[axis] = data
        }
      })

      const xData = axisMap.X
      const yData = axisMap.Y
      const zData = axisMap.Z

      if (!xData || !yData || !zData) {
        return [this.buildNormalizedChartItem(selected)].filter(Boolean)
      }

      const timeMap = new Map()

      const appendAxis = (axis, data) => {
        const labels = data.labels || []
        const values = data.values || []

        labels.forEach((time, idx) => {
          if (!timeMap.has(time)) {
            timeMap.set(time, {
              time,
              x: null,
              y: null,
              z: null
            })
          }

          const row = timeMap.get(time)
          row[axis.toLowerCase()] = values[idx]?.amount ?? null
        })
      }

      appendAxis('X', xData)
      appendAxis('Y', yData)
      appendAxis('Z', zData)

      const rows = Array.from(timeMap.values())
        .filter(row => row.x !== null && row.y !== null && row.z !== null)

      const labels = rows.map(row => row.time)

      const values = rows.map(row => {
        const x = Number(row.x || 0)
        const y = Number(row.y || 0)
        const z = Number(row.z || 0)
        const pvs = Math.sqrt((x * x) + (y * y) + (z * z))

        return {
          x,
          y,
          z,
          pvs,
          amount: pvs
        }
      })

      return [
        this.normalizeChartDisplayMeta({
          sensorId: selectedData.sensorId || selected.sensorId,
          sensorName: 'PVS(kine)',
          sensorCode: representativeCode,
          groupName: selectedData.groupName || selected.groupName || '',
          subGroupName: selectedData.subGroupName || selected.subGroupName || '',
          displayType: 'V3',
          labels,
          values,
          baseMeasuredAt: labels[0] || '',
          basePoint: values[0] || null,
          showAmount: true,
          showDeg: false,
          showMm: false,
          showDiff: false,
          showAngular: false,
          showV3: true,
          amountLabel: 'PVS(kine)',
          chartValueKey: 'amount',
          chartMin: selectedData.chartMin ?? 0,
          chartMax: selectedData.chartMax ?? 0.5,
          thresholdLines: selectedData.thresholdLines || []
        })
      ]
    },

    normalizeListResult(res) {
      const result = res?.data?.result

      if (Array.isArray(result?.content)) {
        return result.content
      }

      if (Array.isArray(result)) {
        return result
      }

      return []
    },

    async fetchAdminFilterOptions() {
      if (!this.isAdminSiteFilterMode) {
        this.clientOptions = []
        this.agencyOptions = []
        return
      }

      try {
        const [clientRes, agencyRes] = await Promise.all([
          api.get('/app/client/list', {
            params: {
              page: 0,
              size: 1000,
              deleted: false,
              orderBy: 'createdAtAsc',
            },
          }),
          api.get('/app/agency/list', {
            params: {
              page: 0,
              size: 1000,
              deleted: false,
              orderBy: 'createdAtAsc',
            },
          }),
        ])

        this.clientOptions = this.normalizeListResult(clientRes).map(item => ({
          id: item.id,
          name: item.name,
        }))

        this.agencyOptions = this.normalizeListResult(agencyRes).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        console.error('관리자 발주처/대리점 목록 조회 실패', e)
        this.clientOptions = []
        this.agencyOptions = []
      }
    },

    async onClientFilterChange(clientId) {
      this.selectedClientId = clientId || null
      await this.reloadSiteListByAdminFilter()
    },

    async onAgencyFilterChange(agencyId) {
      this.selectedAgencyId = agencyId || null
      await this.reloadSiteListByAdminFilter()
    },

    async reloadSiteListByAdminFilter() {
      await this.fetchSiteList()

      const firstArea = this.areas?.[0] || null
      await this.onAreaChange(firstArea)
    },
  }
}
</script>

<style scoped>
.client-view {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f3f6fa;
}

.content-body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.center-area {
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 10px;
  box-sizing: border-box;
}

.sensor-layout {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  gap: 10px;
  padding: 10px;
  box-sizing: border-box;
  overflow: hidden;
}

.sensor-empty-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #fff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* =========================
   LOADING
========================= */

.sensor-loading-panel,
.sensor-data-loading-panel {
  flex: 1;
  min-width: 0;
  min-height: 360px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);

  color: #334155;
  text-align: center;
}

.sensor-loading-panel {
  margin-left: 0;
}

.loading-spinner {
  width: 34px;
  height: 34px;
  margin-bottom: 14px;

  border: 3px solid #e5e7eb;
  border-top-color: var(--color-primary);
  border-radius: 50%;

  animation: loading-spin 0.8s linear infinite;
}

.loading-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}

.loading-desc {
  font-size: 13px;
  color: #64748b;
}

@keyframes loading-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>